import { useLocation, Navigate, Outlet, Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useAuth } from "../hooks/useAuth";
import { authInitializedAtom } from "../store/authAtoms";
import PageLoading from "./PageLoading";
import { useOrganizationQuery } from "../api/queries";
import { useUserPageScopesQuery } from "../api/queries/userQueries";
import type { PageScope, UserRole } from "../utils/types";
import { extractMfaClaim, getTokenClaims } from "../utils/authStorage";

type RequireAuthProps = {
  allowedRoles?: UserRole[];
  requireOrganization?: boolean;
  /** User must have every listed scope (AND). */
  requiredScopes?: PageScope[];
  /** Validate scopes against live DB values (for primary routes). */
  enforceLiveScopeCheck?: boolean;
};

const RequireAuth = ({
  allowedRoles,
  requireOrganization = true,
  requiredScopes,
  enforceLiveScopeCheck = false,
}: RequireAuthProps) => {
  const { roles, token, scopes, mfaClaim } = useAuth();
  const authInitialized = useAtomValue(authInitializedAtom);
  const location = useLocation();

  // Only check organization if user is authenticated
  // Must call hooks before any conditional returns to follow Rules of Hooks
  const isAuthenticated = !!token;
  const {
    data: organization,
    isLoading: isOrganizationLoading,
    error: organizationError,
    refetch: refetchOrganization,
  } = useOrganizationQuery({
    enabled: requireOrganization && isAuthenticated && authInitialized,
  });
  const {
    data: liveScopes,
    isLoading: isLiveScopesLoading,
    error: liveScopesError,
  } = useUserPageScopesQuery(
    enforceLiveScopeCheck &&
      isAuthenticated &&
      authInitialized &&
      Boolean(requiredScopes?.length),
  );

  // Wait for auth to initialize before making any decisions
  // This prevents race conditions with stale localStorage tokens
  if (!authInitialized) {
    return <PageLoading />;
  }

  const hasAllowedRole = allowedRoles
    ? roles?.some((role) => allowedRoles.includes(role as UserRole))
    : true;

  // If user is authenticated, check MFA then organization
  if (isAuthenticated) {
    // Redirect to MFA flow when either the session status or JWT says pending.
    const tokenMfaClaim = token
      ? extractMfaClaim(getTokenClaims(token) ?? {})
      : null;
    const resolvedMfaClaim = mfaClaim ?? tokenMfaClaim;
    if (resolvedMfaClaim === "pending") {
      return <Navigate to="/mfa" state={{ from: location }} replace />;
    }

    if (!hasAllowedRole) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }

    if (requiredScopes?.length) {
      // On primary routes we can validate using live DB scopes to reflect admin changes immediately.
      if (enforceLiveScopeCheck && isLiveScopesLoading) {
        return <PageLoading />;
      }
      // Transient live-scope failures (network, MFA race after org switch) should not
      // hard-redirect to /unauthorized — fall back to JWT/session scopes instead.
      const sourceScopes =
        enforceLiveScopeCheck && !liveScopesError && Array.isArray(liveScopes)
          ? liveScopes
          : (scopes ?? []);
      const scopeSet = new Set(sourceScopes);
      const missing = requiredScopes.filter((s) => !scopeSet.has(s));
      if (missing.length > 0) {
        return (
          <Navigate to="/unauthorized" state={{ from: location }} replace />
        );
      }
    }

    if (!requireOrganization) {
      return <Outlet />;
    }

    // Wait for organization check to complete
    if (isOrganizationLoading) {
      return <PageLoading />;
    }

    // Check if user lacks organization (404 with ORG_NOT_FOUND code)
    const isOrgNotFound =
      organizationError &&
      (organizationError as any)?.response?.status === 404 &&
      (organizationError as any)?.response?.data?.detail?.code ===
        "ORG_NOT_FOUND";

    // If user doesn't have an organization, redirect to unauthorized
    if (isOrgNotFound) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }

    // Transient/other org error (e.g. 500, network): show retry UI instead of redirecting
    if (organizationError && !isOrgNotFound) {
      return (
        <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-gray-600">
            Something went wrong loading your organization.
          </p>
          <button
            type="button"
            onClick={() => refetchOrganization()}
            className="rounded-full bg-orange-500 px-6 py-2.5 font-semibold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
          >
            Try again
          </button>
          <Link
            to="/unauthorized"
            state={{ from: location }}
            className="text-sm text-gray-500 underline hover:text-gray-700"
          >
            I still can’t access the app
          </Link>
        </div>
      );
    }

    // User must have an organization to proceed
    // If query completed successfully but no data, something went wrong
    if (!organization) {
      return <PageLoading />;
    }

    // User is authenticated, has organization, and has allowed role
    return <Outlet />;
  }

  // User is not authenticated
  return <Navigate to="/login" state={{ from: location }} replace />;
};

export default RequireAuth;
